// Web Audio engine for The Studio: synth instruments, drum synthesis, mixer channels, sends, scheduler
const mtof = (p) => 440 * Math.pow(2, (p - 69) / 12);

class Engine {
  constructor() {
    this.ctx = null;
    this.channels = new Map();
    this.playing = false;
    this.queue = [];
    this.getSong = () => null;
    this.listeners = new Set();
    this.metronome = false;
    this.onRecordNote = null;
  }

  init() {
    if (this.ctx) { if (this.ctx.state === "suspended") this.ctx.resume(); return; }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.comp = ctx.createDynamicsCompressor();
    this.comp.threshold.value = -10; this.comp.ratio.value = 3;
    this.masterAnalyser = ctx.createAnalyser(); this.masterAnalyser.fftSize = 1024;
    this.master.connect(this.comp); this.comp.connect(this.masterAnalyser); this.masterAnalyser.connect(ctx.destination);
    // FX returns
    this.reverb = ctx.createConvolver(); this.reverb.buffer = this.impulse(2.4);
    this.reverbRet = ctx.createGain(); this.reverbRet.gain.value = 0.8;
    this.reverb.connect(this.reverbRet); this.reverbRet.connect(this.master);
    this.delay = ctx.createDelay(2); this.delayFb = ctx.createGain(); this.delayFb.gain.value = 0.35;
    this.delayRet = ctx.createGain(); this.delayRet.gain.value = 0.6;
    this.delay.connect(this.delayFb); this.delayFb.connect(this.delay); this.delay.connect(this.delayRet); this.delayRet.connect(this.master);
    this.noise = this.makeNoise();
  }

  impulse(sec) {
    const ctx = this.ctx, len = ctx.sampleRate * sec, b = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let c = 0; c < 2; c++) { const d = b.getChannelData(c); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3); }
    return b;
  }
  makeNoise() {
    const ctx = this.ctx, b = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate), d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return b;
  }

  channel(track) {
    let ch = this.channels.get(track.id);
    if (ch) return ch;
    const ctx = this.ctx;
    ch = {
      input: ctx.createGain(),
      low: ctx.createBiquadFilter(), mid: ctx.createBiquadFilter(), high: ctx.createBiquadFilter(),
      pan: ctx.createStereoPanner(), fader: ctx.createGain(), analyser: ctx.createAnalyser(),
      rev: ctx.createGain(), dly: ctx.createGain(),
    };
    ch.low.type = "lowshelf"; ch.low.frequency.value = 220;
    ch.mid.type = "peaking"; ch.mid.frequency.value = 1200; ch.mid.Q.value = 0.9;
    ch.high.type = "highshelf"; ch.high.frequency.value = 5000;
    ch.analyser.fftSize = 512;
    ch.input.connect(ch.low); ch.low.connect(ch.mid); ch.mid.connect(ch.high); ch.high.connect(ch.pan);
    ch.pan.connect(ch.fader); ch.fader.connect(ch.analyser); ch.analyser.connect(this.master);
    ch.fader.connect(ch.rev); ch.rev.connect(this.reverb); ch.fader.connect(ch.dly); ch.dly.connect(this.delay);
    this.channels.set(track.id, ch);
    return ch;
  }

  sync(song) {
    if (!this.ctx || !song) return;
    const anySolo = song.tracks.some((t) => t.solo);
    const ids = new Set(song.tracks.map((t) => t.id));
    for (const [id, ch] of this.channels) if (!ids.has(id)) { ch.analyser.disconnect(); ch.rev.disconnect(); ch.dly.disconnect(); this.channels.delete(id); }
    const t0 = this.ctx.currentTime;
    song.tracks.forEach((t) => {
      const ch = this.channel(t);
      const audible = !t.mute && (!anySolo || t.solo);
      ch.fader.gain.setTargetAtTime(audible ? t.volume * t.volume : 0, t0, 0.015);
      ch.pan.pan.setTargetAtTime(t.pan, t0, 0.015);
      const eq = t.eqOn === false ? { low: 0, mid: 0, high: 0 } : t.eq || { low: 0, mid: 0, high: 0 };
      ch.low.gain.value = eq.low; ch.mid.gain.value = eq.mid; ch.high.gain.value = eq.high;
      ch.rev.gain.value = t.sendRev || 0; ch.dly.gain.value = t.sendDly || 0;
    });
    this.master.gain.setTargetAtTime(song.masterVol * song.masterVol, t0, 0.015);
    this.delay.delayTime.value = (60 / song.bpm) * 0.75;
  }

  level(analyser) {
    if (!analyser) return 0;
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    let peak = 0; for (let i = 0; i < buf.length; i++) { const a = Math.abs(buf[i]); if (a > peak) peak = a; }
    return peak;
  }
  trackLevel(id) { const ch = this.channels.get(id); return ch ? this.level(ch.analyser) : 0; }
  masterLevel() { return this.masterAnalyser ? this.level(this.masterAnalyser) : 0; }

  env(g, t, a, peak, d, s, dur, r) {
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + a);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak * s), t + a + d);
    const end = t + Math.max(dur, a + d);
    g.gain.setValueAtTime(Math.max(0.0001, peak * s), end);
    g.gain.exponentialRampToValueAtTime(0.0001, end + r);
    return end + r + 0.05;
  }

  noiseSrc(t, dur, type, freq, gain, q = 1) {
    const ctx = this.ctx, src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
    src.buffer = this.noise; f.type = type; f.frequency.value = freq; f.Q.value = q;
    g.gain.setValueAtTime(gain, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f); f.connect(g); src.start(t); src.stop(t + dur + 0.05);
    return g;
  }

  drum(out, pitch, t, v) {
    const ctx = this.ctx;
    const tone = (f0, f1, dur, type = "sine", gain = 1) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = type; o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(f1, t + dur * 0.4);
      g.gain.setValueAtTime(gain * v, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g); g.connect(out); o.start(t); o.stop(t + dur + 0.05);
    };
    switch (pitch) {
      case 36: tone(160, 42, 0.45, "sine", 1.1); break;
      case 38: tone(200, 160, 0.12, "triangle", 0.5); this.noiseSrc(t, 0.2, "highpass", 1200, 0.6 * v).connect(out); break;
      case 37: tone(1800, 1600, 0.04, "square", 0.25); break;
      case 39: [0, 0.012, 0.024].forEach((o, i) => this.noiseSrc(t + o, i === 2 ? 0.22 : 0.03, "bandpass", 1400, 0.8 * v, 1.2).connect(out)); break;
      case 42: this.noiseSrc(t, 0.05, "highpass", 7500, 0.35 * v).connect(out); break;
      case 46: this.noiseSrc(t, 0.32, "highpass", 7000, 0.3 * v).connect(out); break;
      case 45: tone(180, 90, 0.35, "sine", 0.8); break;
      case 48: tone(260, 140, 0.3, "sine", 0.8); break;
      case 49: this.noiseSrc(t, 1.4, "highpass", 5000, 0.28 * v).connect(out); break;
      default: tone(300, 200, 0.2);
    }
  }

  note(track, pitch, t, dur, v = 0.8) {
    if (!this.ctx) return;
    const ch = this.channel(track), out = ch.input, ctx = this.ctx;
    if (track.instrument === "drums") return this.drum(out, pitch, t, v);
    const f = mtof(pitch + (track.transpose || 0));
    const vg = ctx.createGain(); const flt = ctx.createBiquadFilter(); flt.type = "lowpass";
    flt.connect(vg); vg.connect(out);
    let oscs = [], end;
    const mk = (type, freq, det = 0, gain = 1) => { const o = ctx.createOscillator(), g = ctx.createGain(); o.type = type; o.frequency.value = freq; o.detune.value = det; g.gain.value = gain; o.connect(g); g.connect(flt); oscs.push(o); };
    const cut = track.cutoff ?? 1;
    switch (track.instrument) {
      case "bass":
        mk("sawtooth", f, 0, 0.5); mk("square", f / 2, 0, 0.35);
        flt.frequency.setValueAtTime(200 + 2400 * cut, t); flt.frequency.exponentialRampToValueAtTime(120 + 500 * cut, t + 0.25); flt.Q.value = 6;
        end = this.env(vg, t, 0.005, 0.5 * v, 0.2, 0.6, dur, 0.08); break;
      case "pad":
        [-9, 0, 9].forEach((d) => mk("sawtooth", f, d, 0.22)); mk("sine", f / 2, 0, 0.2);
        flt.frequency.value = 400 + 3000 * cut; flt.Q.value = 1;
        end = this.env(vg, t, 0.35, 0.28 * v, 0.4, 0.8, dur, 0.9); break;
      case "lead":
        mk("sawtooth", f, -6, 0.3); mk("square", f, 6, 0.2);
        flt.frequency.value = 800 + 5000 * cut; flt.Q.value = 4;
        end = this.env(vg, t, 0.01, 0.3 * v, 0.15, 0.7, dur, 0.15); break;
      case "pluck":
        mk("triangle", f, 0, 0.6); mk("sawtooth", f * 2, 3, 0.15);
        flt.frequency.setValueAtTime(600 + 6000 * cut, t); flt.frequency.exponentialRampToValueAtTime(300, t + 0.3);
        end = this.env(vg, t, 0.002, 0.45 * v, 0.35, 0.05, 0.05, 0.2); break;
      default: // keys (e-piano)
        mk("sine", f, 0, 0.7); mk("triangle", f * 2, 2, 0.12); mk("sine", f * 4, 0, 0.04);
        flt.frequency.value = 1500 + 6000 * cut;
        end = this.env(vg, t, 0.004, 0.4 * v, 1.1, 0.25, dur, 0.35);
    }
    oscs.forEach((o) => { o.start(t); o.stop(end); });
  }

  click(t, accent) {
    const o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.frequency.value = accent ? 1760 : 1175; o.type = "square";
    g.gain.setValueAtTime(0.18, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    o.connect(g); g.connect(this.masterAnalyser); o.start(t); o.stop(t + 0.06);
  }

  stepDur() { const s = this.getSong(); return 60 / (s ? s.bpm : 120) / 4; }

  play(fromStep = 0) {
    this.init();
    this.sync(this.getSong());
    this.playing = true;
    this.nextStep = Math.floor(fromStep);
    this.nextTime = this.ctx.currentTime + 0.06;
    this.queue = [];
    this.fresh = true;
    this.sources = [];
    this.timer = setInterval(() => this.schedule(), 25);
    this.schedule();
    this.emit();
  }
  stop() {
    this.playing = false;
    clearInterval(this.timer);
    (this.sources || []).forEach((s) => { try { s.stop(); } catch (e) { /* already stopped */ } });
    this.sources = [];
    this.emit();
  }
  emit() { this.listeners.forEach((l) => l(this.playing)); }

  async loadAudio(fileId, url) {
    this.init();
    if (!this.buffers) this.buffers = new Map();
    if (this.buffers.has(fileId)) return this.buffers.get(fileId);
    const decode = (ab) => new Promise((res, rej) => { try { const r = this.ctx.decodeAudioData(ab, res, (e) => rej(e || new Error("decode failed"))); if (r && r.then) r.then(res, rej); } catch (e) { rej(e); } });
    const p = fetch(url).then((r) => { if (!r.ok) throw new Error(`Could not download audio (HTTP ${r.status})`); return r.arrayBuffer(); })
      .then((ab) => decode(ab).catch(() => { throw new Error("This file could not be decoded — try exporting it as MP3 (constant bitrate), WAV or AIFF"); }));
    this.buffers.set(fileId, p);
    try { const buf = await p; this.buffers.set(fileId, buf); return buf; } catch (e) { this.buffers.delete(fileId); throw e; }
  }
  bufferFor(fileId) { const b = this.buffers?.get(fileId); return b instanceof AudioBuffer ? b : null; }

  audioClip(track, clip, t, offsetSteps) {
    const buf = this.bufferFor(clip.audio?.fileId);
    if (!buf) return;
    const ch = this.channel(track), src = this.ctx.createBufferSource();
    src.buffer = buf;
    const a = clip.audio, sd = this.stepDur(), gain = a.gain ?? 1;
    const offSec = offsetSteps * sd, clipDur = clip.length * 16 * sd;
    const fileOff = (a.offset || 0) + offSec;
    if (fileOff >= buf.duration) return;
    const remain = Math.min(clipDur - offSec, buf.duration - fileOff);
    const g = this.ctx.createGain();
    const fi = Math.min(a.fadeIn || 0, clipDur), fo = Math.min(a.fadeOut || 0, clipDur);
    g.gain.setValueAtTime(fi > 0 ? gain * Math.min(1, offSec / fi) : gain, t);
    if (fi > offSec) g.gain.linearRampToValueAtTime(gain, t + (fi - offSec));
    const endT = t + remain, foStart = t + (clipDur - offSec) - fo;
    if (fo > 0 && foStart < endT) { const startG = foStart >= t ? gain : gain * ((endT - t) / fo); g.gain.setValueAtTime(startG, Math.max(t, foStart)); g.gain.linearRampToValueAtTime(0.0001, endT); }
    src.connect(g); g.connect(ch.input);
    src.start(t, fileOff, remain);
    this.sources.push(src);
    src.onended = () => { this.sources = this.sources.filter((s) => s !== src); };
  }

  schedule() {
    const song = this.getSong();
    if (!song) return;
    const endStep = song.bars * 16;
    while (this.nextTime < this.ctx.currentTime + 0.12) {
      const s = this.nextStep, t = this.nextTime;
      this.queue.push({ s, t });
      if (this.metronome && s % 4 === 0) this.click(t, s % 16 === 0);
      const sd = this.stepDur();
      const fresh = this.fresh; this.fresh = false;
      song.tracks.forEach((tr) => {
        tr.clips.forEach((c) => {
          const cs = c.start * 16;
          if (s < cs || s >= cs + c.length * 16 || c.muted) return;
          const local = s - cs;
          if (c.audio) { if (s === Math.ceil(cs) || fresh) this.audioClip(tr, c, t, local); return; }
          c.notes.forEach((n) => { if (n.s === local) this.note(tr, n.p, t, n.l * sd, n.v ?? 0.8); });
        });
      });
      this.nextTime += sd;
      this.nextStep++;
      if (song.loopOn && this.nextStep >= song.loop[1] * 16) { this.nextStep = song.loop[0] * 16; this.fresh = true; }
      else if (this.nextStep >= endStep) { this.nextStep = 0; if (!song.loopOn) { setTimeout(() => this.stop(), (t - this.ctx.currentTime) * 1000 + 100); break; } this.fresh = true; }
    }
    const now = this.ctx.currentTime;
    while (this.queue.length > 2 && this.queue[1].t <= now) this.queue.shift();
  }

  position() {
    if (!this.playing || !this.queue.length || !this.ctx) return null;
    const now = this.ctx.currentTime;
    let cur = this.queue[0];
    for (const q of this.queue) if (q.t <= now) cur = q;
    const frac = Math.min(1, Math.max(0, (now - cur.t) / this.stepDur()));
    return cur.s + frac;
  }

  preview(track, pitch, dur = 0.4) { this.init(); this.sync(this.getSong()); this.note(track, pitch, this.ctx.currentTime + 0.01, dur); }

  async recordMixdown(song, onDone) {
    this.init();
    const dest = this.ctx.createMediaStreamDestination();
    this.masterAnalyser.connect(dest);
    const rec = new MediaRecorder(dest.stream);
    const chunks = [];
    rec.ondataavailable = (e) => chunks.push(e.data);
    rec.onstop = () => { this.masterAnalyser.disconnect(dest); onDone(new Blob(chunks, { type: rec.mimeType || "audio/webm" })); };
    const bars = song.loopOn ? song.loop[1] - song.loop[0] : song.bars;
    const from = song.loopOn ? song.loop[0] * 16 : 0;
    rec.start();
    this.play(from);
    const ms = bars * 4 * (60 / song.bpm) * 1000 + 1500;
    setTimeout(() => { this.stop(); rec.stop(); }, ms);
    return ms;
  }
}

export const engine = new Engine();
export const DRUM_ROWS = [
  { p: 49, n: "Crash" }, { p: 46, n: "Open Hat" }, { p: 42, n: "Closed Hat" }, { p: 48, n: "Hi Tom" },
  { p: 45, n: "Lo Tom" }, { p: 39, n: "Clap" }, { p: 37, n: "Rim" }, { p: 38, n: "Snare" }, { p: 36, n: "Kick" },
];
export const INSTRUMENTS = [
  { id: "drums", name: "Impact XT Kit", desc: "Drum machine" },
  { id: "bass", name: "Mai Tai Bass", desc: "Analog mono bass" },
  { id: "keys", name: "Presence E-Piano", desc: "Electric piano" },
  { id: "pad", name: "Mojito Pad", desc: "Wide poly pad" },
  { id: "lead", name: "Sawtooth Lead", desc: "Detuned lead" },
  { id: "pluck", name: "Pluck Synth", desc: "Bright pluck" },
];
export const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
export const noteName = (p) => NOTE_NAMES[p % 12] + (Math.floor(p / 12) - 1);
